/**
 * Client-only identity and entry attribution for first-party analytics.
 *
 * `la_sid` is deliberately scoped to one browser session so funnel events can
 * be joined accurately. `la_vid` and the first-touch data last longer, but
 * contain only coarse campaign and referrer fields, never personal data.
 */

import {
  appendHandoffParams,
  buildHandoffParams,
  cleanAttributionValue,
  readHandoffParams,
  resolveHandoffVisitor,
  stripHandoffParams,
  type HandoffFrom,
} from "@/lib/analytics/handoff";

const SESSION_KEY = "la_sid";
const VISITOR_KEY = "la_vid";
const FIRST_TOUCH_KEY = "la_first_touch";
/** This browser session's entry source/medium/campaign (first computed value wins). */
const SESSION_ATTRIBUTION_KEY = "la_session_attr";
const FIRST_TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export interface AcquisitionAttribution {
  sessionId: string | null;
  visitorId: string | null;
  source: string;
  medium: string;
  campaign: string | null;
  term: string | null;
  content: string | null;
  landingPath: string;
  referrerHost: string | null;
  firstSource: string;
  firstMedium: string;
  firstCampaign: string | null;
  isFirstTouch: boolean;
  /** True when this page load arrived through an in-app → external browser handoff. */
  isHandoff: boolean;
  /** In-app browser type the visitor was handed off from (la_from). */
  handoffFrom: HandoffFrom | null;
  /** Incoming visitor id when this browser kept its own la_vid instead of adopting it. */
  handoffVisitorId: string | null;
}

interface SessionAttribution {
  source: string;
  medium: string;
  campaign: string | null;
}

interface StoredFirstTouch {
  source: string;
  medium: string;
  campaign: string | null;
  capturedAt: number;
}

function createId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getStorageId(storage: Storage, key: string): string {
  let id = storage.getItem(key);
  if (!id) {
    id = createId();
    storage.setItem(key, id);
  }
  return id;
}

function matchesDomain(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

function sourceFromReferrer(host: string): { source: string; medium: string } {
  const normalized = host.replace(/^www\./, "").toLowerCase();

  // AI answer engines need to be checked before Google because Gemini lives
  // under google.com and would otherwise look like a regular organic search.
  if (matchesDomain(normalized, "chatgpt.com") || matchesDomain(normalized, "chat.openai.com")) {
    return { source: "chatgpt", medium: "ai" };
  }
  if (matchesDomain(normalized, "perplexity.ai")) return { source: "perplexity", medium: "ai" };
  if (matchesDomain(normalized, "claude.ai")) return { source: "claude", medium: "ai" };
  if (matchesDomain(normalized, "gemini.google.com")) return { source: "gemini", medium: "ai" };
  if (matchesDomain(normalized, "copilot.microsoft.com")) return { source: "copilot", medium: "ai" };
  if (matchesDomain(normalized, "grok.com")) return { source: "grok", medium: "ai" };
  if (matchesDomain(normalized, "you.com")) return { source: "you.com", medium: "ai" };
  if (matchesDomain(normalized, "phind.com")) return { source: "phind", medium: "ai" };

  if (matchesDomain(normalized, "instagram.com")) return { source: "instagram", medium: "social" };
  if (matchesDomain(normalized, "facebook.com") || matchesDomain(normalized, "fb.com")) {
    return { source: "facebook", medium: "social" };
  }
  if (
    matchesDomain(normalized, "x.com") ||
    matchesDomain(normalized, "twitter.com") ||
    matchesDomain(normalized, "t.co")
  ) {
    return { source: "x", medium: "social" };
  }
  if (matchesDomain(normalized, "tiktok.com")) return { source: "tiktok", medium: "social" };
  if (matchesDomain(normalized, "youtube.com") || matchesDomain(normalized, "youtu.be")) {
    return { source: "youtube", medium: "social" };
  }
  if (matchesDomain(normalized, "threads.net")) return { source: "threads", medium: "social" };
  if (matchesDomain(normalized, "linkedin.com")) return { source: "linkedin", medium: "social" };
  if (matchesDomain(normalized, "kakao.com")) return { source: "kakao", medium: "social" };
  if (matchesDomain(normalized, "line.me")) return { source: "line", medium: "social" };
  if (matchesDomain(normalized, "reddit.com")) return { source: "reddit", medium: "social" };

  if (/(^|\.)google\./.test(normalized)) return { source: "google", medium: "organic" };
  if (/(^|\.)bing\./.test(normalized)) return { source: "bing", medium: "organic" };
  if (/(^|\.)search\.naver\./.test(normalized)) {
    return { source: "naver", medium: "organic" };
  }
  if (/(^|\.)search\.daum\./.test(normalized)) {
    return { source: "daum", medium: "organic" };
  }
  return { source: normalized, medium: "referral" };
}

function readFirstTouch(storage: Storage): StoredFirstTouch | null {
  const stored = storage.getItem(FIRST_TOUCH_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as StoredFirstTouch;
    if (
      typeof parsed.capturedAt === "number" &&
      Date.now() - parsed.capturedAt < FIRST_TOUCH_TTL_MS
    ) {
      return parsed;
    }
    return null;
  } catch {
    storage.removeItem(FIRST_TOUCH_KEY);
    return null;
  }
}

function readSessionAttribution(): SessionAttribution | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_ATTRIBUTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionAttribution;
    return typeof parsed?.source === "string" ? parsed : null;
  } catch {
    return null;
  }
}

/** Keeps the session's entry attribution; later page loads (no referrer) never overwrite it. */
function rememberSessionAttribution(value: SessionAttribution): void {
  try {
    if (window.sessionStorage.getItem(SESSION_ATTRIBUTION_KEY)) return;
    window.sessionStorage.setItem(SESSION_ATTRIBUTION_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable — the handoff falls back to the in-app source.
  }
}

/** Current anonymous visitor id (localStorage la_vid), created when missing. */
export function getVisitorId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return getStorageId(window.localStorage, VISITOR_KEY);
  } catch {
    return null;
  }
}

/**
 * Adds the visitor handoff params to a same-origin URL that is about to be
 * opened in the external browser. Every in-app → external handoff passes
 * through getOpenExternalPlan, which calls this before wrapping the URL in a
 * scheme. Other origins are returned unchanged so la_vid never leaks.
 */
export function withHandoffParams(targetUrl: string, from: HandoffFrom): string {
  if (typeof window === "undefined") return targetUrl;
  try {
    if (new URL(targetUrl).origin !== window.location.origin) return targetUrl;
    const session = readSessionAttribution();
    return appendHandoffParams(
      targetUrl,
      buildHandoffParams({
        visitorId: getVisitorId(),
        from,
        source: session?.source ?? null,
        medium: session?.medium ?? null,
        campaign: session?.campaign ?? null,
      }),
    );
  } catch {
    return targetUrl;
  }
}

/** Removes handoff params from the address bar so they are not shared or re-read. */
function stripHandoffParamsFromAddressBar(): void {
  const stripped = stripHandoffParams(window.location.href);
  if (!stripped) return;
  try {
    // Next patches replaceState: passing the current state (no __NA flag)
    // updates the router's canonical URL too, so a later router.refresh()
    // cannot write the stripped params back into the address bar.
    window.history.replaceState(window.history.state, "", stripped);
  } catch {
    // Leaving the params in place only costs a cosmetic URL.
  }
}

export function getSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return getStorageId(window.sessionStorage, SESSION_KEY);
  } catch {
    return null;
  }
}

export function getAcquisitionAttribution(): AcquisitionAttribution | null {
  if (typeof window === "undefined") return null;

  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const utmSource = cleanAttributionValue(params.get("utm_source"));
    const utmMedium = cleanAttributionValue(params.get("utm_medium"));
    const utmCampaign = cleanAttributionValue(params.get("utm_campaign"));
    const term = cleanAttributionValue(params.get("utm_term"));
    const content = cleanAttributionValue(params.get("utm_content"));
    const hasGoogleClickId = Boolean(
      params.get("gclid") || params.get("gbraid") || params.get("wbraid"),
    );

    let referrerHost: string | null = null;
    try {
      const referrer = document.referrer ? new URL(document.referrer) : null;
      if (referrer && referrer.origin !== url.origin) referrerHost = referrer.host;
    } catch {
      // A malformed referrer is not useful for attribution.
    }

    const handoff = readHandoffParams(params);

    const inferred = referrerHost
      ? sourceFromReferrer(referrerHost)
      : { source: "direct", medium: "direct" };
    let source = utmSource ?? (hasGoogleClickId ? "google" : inferred.source);
    let medium = utmMedium ?? (hasGoogleClickId ? "cpc" : inferred.medium);
    let campaign = utmCampaign;
    // The in-app session already resolved the real entry source; the external
    // browser itself only sees a referrer-less (direct) load.
    if (handoff?.source) {
      source = handoff.source;
      medium = handoff.medium ?? "inapp";
      campaign = handoff.campaign;
    }

    let firstTouch = readFirstTouch(window.localStorage);
    const { adopt, handoffVisitorId } = resolveHandoffVisitor({
      incomingVisitorId: handoff?.visitorId ?? null,
      localVisitorId: window.localStorage.getItem(VISITOR_KEY),
      hasFirstTouch: Boolean(firstTouch),
    });
    if (adopt && handoff?.visitorId) {
      window.localStorage.setItem(VISITOR_KEY, handoff.visitorId);
    }

    // An adopted visitor's first touch was already recorded by the in-app session.
    const isFirstTouch = !firstTouch && !adopt;
    if (!firstTouch) {
      firstTouch = { source, medium, campaign, capturedAt: Date.now() };
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(firstTouch));
    }

    rememberSessionAttribution({ source, medium, campaign });
    if (handoff) stripHandoffParamsFromAddressBar();

    return {
      sessionId: getStorageId(window.sessionStorage, SESSION_KEY),
      visitorId: getStorageId(window.localStorage, VISITOR_KEY),
      source,
      medium,
      campaign,
      term,
      content,
      landingPath: url.pathname,
      referrerHost,
      firstSource: firstTouch.source,
      firstMedium: firstTouch.medium,
      firstCampaign: firstTouch.campaign,
      isFirstTouch,
      isHandoff: Boolean(handoff),
      handoffFrom: handoff?.from ?? null,
      handoffVisitorId,
    };
  } catch {
    return null;
  }
}
